import React, { useState } from 'react';
import { X, Crown, Sparkles, Check, TrendingDown } from 'lucide-react';
import { Button } from './ui/button';

const TrialExpiredModal = ({ isOpen, onClose, onUpgrade }) => {
  const [billingPeriod, setBillingPeriod] = useState('annual');

  const handleUpgrade = async () => {
    if (onUpgrade) {
      onUpgrade(billingPeriod);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-3xl bg-gradient-to-br from-pink-50 via-white to-purple-50 rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header */}
        <div className="p-8 pb-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full mb-4">
            <TrendingDown className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Your Trial Has Ended
          </h2>
          <p className="text-gray-600 text-lg">
            You're now on the Reflect (Free) plan
          </p>
        </div>

        {/* Before & After Comparison */}
        <div className="px-8 pb-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-center mb-4 text-gray-900">
              What Changed?
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* What You Had (Trial) */}
              <div className="space-y-3">
                <p className="font-semibold text-pink-600 text-center mb-3">During Trial 🎉</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="text-gray-700">Unlimited Conchi</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="text-gray-700">Unlimited AI insights</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="text-gray-700">Unlimited affirmations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="text-gray-700">Divine Guidance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="text-gray-700">Vision Board</span>
                  </div>
                </div>
              </div>

              {/* What You Have Now (Free) */}
              <div className="space-y-3">
                <p className="font-semibold text-gray-600 text-center mb-3">Now (Free) 📉</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">10 Conchi msgs/month</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">3 AI insights/month</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">1 affirmation/day</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-400" />
                    <span className="text-gray-400 line-through">Divine Guidance</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-400" />
                    <span className="text-gray-400 line-through">Vision Board</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade CTA */}
        <div className="px-8 pb-6">
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-6 text-white text-center">
            <Crown className="w-12 h-12 text-yellow-300 mx-auto mb-3" />
            <h3 className="text-2xl font-bold mb-2">
              Get Everything Back!
            </h3>
            <p className="text-pink-100 mb-4">
              Upgrade to Growth and continue your transformation journey
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-pink-600'
                    : 'bg-white/20 text-white'
                }`}
              >
                $17/month
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`relative px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-pink-600'
                    : 'bg-white/20 text-white'
                }`}
              >
                $169/year
                <span className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">
                  Save $34
                </span>
              </button>
            </div>

            {billingPeriod === 'annual' && (
              <p className="text-yellow-300 text-sm font-semibold mb-4">
                Just $14.08/month - Get 2 months free!
              </p>
            )}

            <Button
              onClick={handleUpgrade}
              className="w-full py-4 bg-white hover:bg-gray-100 text-pink-600 font-bold rounded-full text-lg shadow-xl"
            >
              Upgrade to Growth →
            </Button>

            <p className="text-pink-100 text-xs mt-3">
              Cancel anytime • No questions asked 💕
            </p>
          </div>
        </div>

        {/* Continue Free Option */}
        <div className="px-8 pb-8">
          <button
            onClick={onClose}
            className="w-full text-center text-gray-500 text-sm hover:text-gray-700 transition-colors"
          >
            Continue with Free Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredModal;
